import { Instrument } from '@/types/production.types';

export const INSTRUMENT_LIBRARY: Instrument[] = [
  // ═══════════════════════════════════════════════════════════════
  // DRUMS
  // ═══════════════════════════════════════════════════════════════
  {
    id: 'drums-rock',
    name: 'Rock Drums',
    category: 'drums',
    subCategory: 'acoustic',
    genres: ['rock', 'southern rock', 'blues rock', 'country rock'],
    stereoPosition: { default: 0, spread: 60, canPan: false, zone: 'wide' },
    frequencyRange: { low: 40, high: 12000, focus: 'full-spectrum', competesWidth: [] },
    role: 'rhythmic foundation',
    promptHints: 'driving rock drums, punchy kick and snare, room ambience',
    mixNotes: 'Kick and snare center, hi-hats slightly left, toms panned L-R, overheads wide'
  },
  {
    id: 'drums-doom',
    name: 'Doom Drums',
    category: 'drums',
    subCategory: 'acoustic',
    genres: ['doom', 'doom metal', 'sludge', 'stoner'],
    stereoPosition: { default: 0, spread: 40, canPan: false, zone: 'center' },
    frequencyRange: { low: 35, high: 10000, focus: 'low-mid', competesWidth: [] },
    role: 'rhythmic foundation',
    promptHints: 'slow, deliberate doom drums, massive reverb, cavern toms',
    mixNotes: 'Everything more mono-focused, massive room sound, slow and heavy'
  },
  {
    id: 'drums-metal',
    name: 'Metal Drums',
    category: 'drums',
    subCategory: 'acoustic',
    genres: ['metal', 'groove metal', 'thrash', 'heavy metal'],
    stereoPosition: { default: 0, spread: 50, canPan: false, zone: 'wide' },
    frequencyRange: { low: 40, high: 14000, focus: 'full-spectrum', competesWidth: [] },
    role: 'rhythmic foundation',
    promptHints: 'tight metal drums, triggered kick, fast double bass, precise attack',
    mixNotes: 'Very tight and punchy, kick locked with bass guitar, crisp snare'
  },
  {
    id: 'drums-country',
    name: 'Country Drums',
    category: 'drums',
    subCategory: 'acoustic',
    genres: ['country', 'outlaw country', 'americana'],
    stereoPosition: { default: 0, spread: 50, canPan: false, zone: 'wide' },
    frequencyRange: { low: 50, high: 12000, focus: 'mid', competesWidth: [] },
    role: 'rhythmic foundation',
    promptHints: 'laid-back country drums, brush snare, train beat, 2/4 feel',
    mixNotes: 'Not too loud, supportive role, snare cracks but doesnt dominate'
  },
  {
    id: 'drums-funk',
    name: 'Funk Drums',
    category: 'drums',
    subCategory: 'acoustic',
    genres: ['funk', 'disco', 'groove'],
    stereoPosition: { default: 0, spread: 55, canPan: false, zone: 'wide' },
    frequencyRange: { low: 45, high: 12000, focus: 'mid', competesWidth: [] },
    role: 'rhythmic foundation',
    promptHints: 'tight funk drums, syncopated hi-hats, ghost notes, pocket groove',
    mixNotes: 'Snare and kick very tight, hi-hats busy, everything locked'
  },
  {
    id: 'drums-shuffle',
    name: 'Shuffle Drums',
    category: 'drums',
    subCategory: 'acoustic',
    genres: ['blues', 'blues rock', 'swing'],
    stereoPosition: { default: 0, spread: 50, canPan: false, zone: 'wide' },
    frequencyRange: { low: 50, high: 11000, focus: 'mid', competesWidth: [] },
    role: 'rhythmic foundation',
    promptHints: 'blues shuffle drums, triplet feel, swinging groove',
    mixNotes: 'Room sound important, natural feel, ride cymbal prominent'
  },
  {
    id: 'drums-arena',
    name: 'Arena Rock Drums',
    category: 'drums',
    subCategory: 'acoustic',
    genres: ['arena rock', 'glam metal', '80s rock'],
    stereoPosition: { default: 0, spread: 70, canPan: false, zone: 'wide' },
    frequencyRange: { low: 40, high: 14000, focus: 'full-spectrum', competesWidth: [] },
    role: 'rhythmic foundation',
    promptHints: 'gated 80s drums, huge reverb, massive snare, stadium sound',
    mixNotes: 'Big gated reverb, snare HUGE, toms boom, very produced'
  },
  {
    id: 'drums-punk',
    name: 'Punk Drums',
    category: 'drums',
    subCategory: 'acoustic',
    genres: ['punk', 'hardcore', 'cowpunk'],
    stereoPosition: { default: 0, spread: 30, canPan: false, zone: 'center' },
    frequencyRange: { low: 50, high: 10000, focus: 'mid', competesWidth: [] },
    role: 'rhythmic foundation',
    promptHints: 'fast punk drums, 4-on-the-floor, relentless energy',
    mixNotes: 'Raw and aggressive, not too refined, fast and loud'
  },
  {
    id: 'drums-ballad',
    name: 'Ballad Drums',
    category: 'drums',
    subCategory: 'acoustic',
    genres: ['ballad', 'soft rock', 'emotional'],
    stereoPosition: { default: 0, spread: 60, canPan: false, zone: 'wide' },
    frequencyRange: { low: 50, high: 11000, focus: 'mid', competesWidth: [] },
    role: 'rhythmic support',
    promptHints: 'soft ballad drums, brushes, tasteful fills, builds in chorus',
    mixNotes: 'Very dynamic, quiet in verses, powerful in choruses only'
  },
  {
    id: 'drums-gospel',
    name: 'Gospel Drums',
    category: 'drums',
    subCategory: 'acoustic',
    genres: ['gospel', 'soul', 'r&b'],
    stereoPosition: { default: 0, spread: 50, canPan: false, zone: 'wide' },
    frequencyRange: { low: 50, high: 12000, focus: 'mid', competesWidth: [] },
    role: 'rhythmic foundation',
    promptHints: 'gospel drums, pocket groove, syncopated fills, building energy',
    mixNotes: 'Locked with bass and keys, dynamic range for builds'
  },

  // ═══════════════════════════════════════════════════════════════
  // BASS
  // ═══════════════════════════════════════════════════════════════
  {
    id: 'bass-electric',
    name: 'Electric Bass',
    category: 'bass',
    subCategory: 'electric',
    genres: ['rock', 'southern rock', 'country rock', 'blues rock'],
    stereoPosition: { default: 0, canPan: false, zone: 'center' },
    frequencyRange: { low: 40, high: 5000, focus: 'low', competesWidth: ['bass-heavy'] },
    role: 'harmonic foundation',
    promptHints: 'driving electric bass, fingerstyle, growly tone',
    mixNotes: 'Dead center, locked with kick drum, warm low end'
  },
  {
    id: 'bass-heavy',
    name: 'Heavy Bass',
    category: 'bass',
    subCategory: 'electric',
    genres: ['metal', 'doom', 'sludge', 'groove metal'],
    stereoPosition: { default: 0, canPan: false, zone: 'center' },
    frequencyRange: { low: 30, high: 4000, focus: 'sub', competesWidth: ['bass-electric'] },
    role: 'harmonic foundation',
    promptHints: 'thunderous heavy bass, distorted, rumbling sub frequencies',
    mixNotes: 'Massive low end, often with slight distortion, anchors everything'
  },
  {
    id: 'bass-heavy-pick',
    name: 'Heavy Pick Bass',
    category: 'bass',
    subCategory: 'electric',
    genres: ['metal', 'punk', 'thrash'],
    stereoPosition: { default: 0, canPan: false, zone: 'center' },
    frequencyRange: { low: 35, high: 6000, focus: 'low-mid', competesWidth: ['bass-heavy'] },
    role: 'harmonic foundation',
    promptHints: 'aggressive pick bass, clanky attack, bright distortion',
    mixNotes: 'More mid-range presence for attack, locked tight with guitars'
  },
  {
    id: 'bass-slap',
    name: 'Slap Bass',
    category: 'bass',
    subCategory: 'electric',
    genres: ['funk', 'disco', 'groove'],
    stereoPosition: { default: 0, canPan: false, zone: 'center' },
    frequencyRange: { low: 40, high: 8000, focus: 'low-mid', competesWidth: [] },
    role: 'harmonic & rhythmic foundation',
    promptHints: 'slap bass, funky pops and thumbs, percussive attack',
    mixNotes: 'Center with presence in mids for the slap attack'
  },
  {
    id: 'bass-punk',
    name: 'Punk Bass',
    category: 'bass',
    subCategory: 'electric',
    genres: ['punk', 'hardcore', 'cowpunk'],
    stereoPosition: { default: 0, canPan: false, zone: 'center' },
    frequencyRange: { low: 50, high: 5000, focus: 'mid', competesWidth: [] },
    role: 'harmonic foundation',
    promptHints: 'fast punk bass, root notes, driving eighth notes',
    mixNotes: 'Not too much sub, more mid presence for the speed'
  },
  {
    id: 'bass-electric-blues',
    name: 'Blues Bass',
    category: 'bass',
    subCategory: 'electric',
    genres: ['blues', 'blues rock', 'swamp blues'],
    stereoPosition: { default: 0, canPan: false, zone: 'center' },
    frequencyRange: { low: 45, high: 4000, focus: 'low', competesWidth: [] },
    role: 'harmonic foundation',
    promptHints: 'warm blues bass, walking lines, supportive groove',
    mixNotes: 'Warm and round, supportive not dominant'
  },
  {
    id: 'bass-gospel',
    name: 'Gospel Bass',
    category: 'bass',
    subCategory: 'electric',
    genres: ['gospel', 'soul', 'r&b'],
    stereoPosition: { default: 0, canPan: false, zone: 'center' },
    frequencyRange: { low: 40, high: 4000, focus: 'low', competesWidth: [] },
    role: 'harmonic foundation',
    promptHints: 'deep gospel bass, pocket groove, fills with choir',
    mixNotes: 'Locked with drums, supportive of vocal builds'
  },
  {
    id: 'bass-electric-soft',
    name: 'Soft Electric Bass',
    category: 'bass',
    subCategory: 'electric',
    genres: ['ballad', 'soft rock'],
    stereoPosition: { default: 0, canPan: false, zone: 'center' },
    frequencyRange: { low: 45, high: 3500, focus: 'low', competesWidth: [] },
    role: 'harmonic support',
    promptHints: 'gentle bass, soft attack, supportive foundation',
    mixNotes: 'Understated, felt more than heard in verses'
  },
  {
    id: 'bass-electric-bright',
    name: 'Bright Bass',
    category: 'bass',
    subCategory: 'electric',
    genres: ['glam', '80s rock', 'pop rock'],
    stereoPosition: { default: 0, canPan: false, zone: 'center' },
    frequencyRange: { low: 50, high: 6000, focus: 'low-mid', competesWidth: [] },
    role: 'harmonic foundation',
    promptHints: 'bright punchy bass, defined attack, cuts through',
    mixNotes: 'More presence in upper frequencies for definition'
  },

  // ═══════════════════════════════════════════════════════════════
  // ELECTRIC GUITARS
  // ═══════════════════════════════════════════════════════════════
  {
    id: 'electric-guitar-rhythm',
    name: 'Rhythm Guitar',
    category: 'rhythm',
    subCategory: 'electric guitar',
    genres: ['rock', 'southern rock', 'blues rock'],
    stereoPosition: { default: -60, canPan: true, zone: 'left' },
    frequencyRange: { low: 80, high: 6000, focus: 'mid', competesWidth: ['electric-guitar-lead'] },
    role: 'harmonic bed',
    promptHints: 'crunchy rhythm guitar, classic rock tone, full chords',
    mixNotes: 'Panned to one side, leaves room for lead on opposite'
  },
  {
    id: 'electric-guitar-lead',
    name: 'Lead Guitar',
    category: 'lead',
    subCategory: 'electric guitar',
    genres: ['rock', 'southern rock', 'blues rock', 'metal'],
    stereoPosition: { default: 60, canPan: true, zone: 'right' },
    frequencyRange: { low: 150, high: 8000, focus: 'high-mid', competesWidth: ['electric-guitar-rhythm'] },
    role: 'melodic lead',
    promptHints: 'screaming lead guitar, expressive bends, singing sustain',
    mixNotes: 'Opposite side from rhythm, slightly more forward'
  },
  {
    id: 'electric-guitar-heavy',
    name: 'Heavy Rhythm Guitar',
    category: 'rhythm',
    subCategory: 'electric guitar',
    genres: ['metal', 'doom', 'sludge', 'stoner'],
    stereoPosition: { default: -50, spread: 100, canPan: true, zone: 'wide' },
    frequencyRange: { low: 70, high: 5000, focus: 'low-mid', competesWidth: ['electric-guitar-doom'] },
    role: 'harmonic bed',
    promptHints: 'crushing heavy guitar, thick distortion, wall of sound',
    mixNotes: 'Often double-tracked and panned hard L/R for huge wall'
  },
  {
    id: 'electric-guitar-doom',
    name: 'Doom Guitar',
    category: 'rhythm',
    subCategory: 'electric guitar',
    genres: ['doom', 'doom metal', 'stoner', 'sludge'],
    stereoPosition: { default: 50, spread: 100, canPan: true, zone: 'wide' },
    frequencyRange: { low: 60, high: 4500, focus: 'low', competesWidth: ['electric-guitar-heavy'] },
    role: 'harmonic bed',
    promptHints: 'slow doom riffs, fuzzy massive tone, droning sustain',
    mixNotes: 'Paired with heavy rhythm, together creates massive wall'
  },
  {
    id: 'electric-guitar-djent',
    name: 'Djent Guitar',
    category: 'rhythm',
    subCategory: 'electric guitar',
    genres: ['groove metal', 'djent', 'progressive metal'],
    stereoPosition: { default: -70, spread: 140, canPan: true, zone: 'wide' },
    frequencyRange: { low: 60, high: 6000, focus: 'low-mid', competesWidth: [] },
    role: 'harmonic & rhythmic bed',
    promptHints: 'tight djent chugs, percussive palm mutes, 7-string attack',
    mixNotes: 'Very tight, locked with kick, aggressive mid presence'
  },
  {
    id: 'electric-guitar-twangy',
    name: 'Twangy Electric',
    category: 'rhythm',
    subCategory: 'electric guitar',
    genres: ['country', 'outlaw country', 'rockabilly'],
    stereoPosition: { default: -45, canPan: true, zone: 'left' },
    frequencyRange: { low: 150, high: 8000, focus: 'high-mid', competesWidth: ['pedal-steel'] },
    role: 'melodic color',
    promptHints: 'telecaster twang, chicken pickin, bright snap',
    mixNotes: 'Bright and present, complementary to acoustic'
  },
  {
    id: 'electric-guitar-80s',
    name: '80s Rock Guitar',
    category: 'rhythm',
    subCategory: 'electric guitar',
    genres: ['glam', '80s rock', 'hair metal'],
    stereoPosition: { default: -65, spread: 130, canPan: true, zone: 'wide' },
    frequencyRange: { low: 100, high: 7000, focus: 'mid', competesWidth: [] },
    role: 'harmonic bed',
    promptHints: 'chorused 80s guitar, layered clean and crunch, big reverb',
    mixNotes: 'Layered, big reverb, wide stereo spread'
  },
  {
    id: 'electric-guitar-lead-shred',
    name: 'Shred Lead Guitar',
    category: 'lead',
    subCategory: 'electric guitar',
    genres: ['glam', '80s rock', 'shred', 'metal'],
    stereoPosition: { default: 0, canPan: true, zone: 'center' },
    frequencyRange: { low: 200, high: 10000, focus: 'high-mid', competesWidth: [] },
    role: 'melodic lead',
    promptHints: 'blazing shred guitar, fast runs, harmonized leads',
    mixNotes: 'Center when soloing, high gain, long reverb tail'
  },
  {
    id: 'electric-guitar-blues',
    name: 'Blues Electric',
    category: 'lead',
    subCategory: 'electric guitar',
    genres: ['blues', 'blues rock', 'swamp blues'],
    stereoPosition: { default: 35, canPan: true, zone: 'right' },
    frequencyRange: { low: 100, high: 7000, focus: 'mid', competesWidth: [] },
    role: 'melodic lead',
    promptHints: 'warm blues guitar, singing bends, tube amp warmth',
    mixNotes: 'Warm, slightly off center, not too bright'
  },
  {
    id: 'electric-guitar-funk',
    name: 'Funk Guitar',
    category: 'rhythm',
    subCategory: 'electric guitar',
    genres: ['funk', 'disco', 'groove'],
    stereoPosition: { default: -40, canPan: true, zone: 'left' },
    frequencyRange: { low: 200, high: 6000, focus: 'high-mid', competesWidth: ['clavinet'] },
    role: 'rhythmic color',
    promptHints: 'scratchy funk guitar, percussive strums, wah pedal',
    mixNotes: 'Very percussive, cuts through, rhythmic focal point'
  },
  {
    id: 'electric-guitar-punk',
    name: 'Punk Guitar',
    category: 'rhythm',
    subCategory: 'electric guitar',
    genres: ['punk', 'hardcore', 'cowpunk'],
    stereoPosition: { default: -30, spread: 60, canPan: true, zone: 'wide' },
    frequencyRange: { low: 100, high: 5000, focus: 'mid', competesWidth: [] },
    role: 'harmonic bed',
    promptHints: 'raw punk guitar, fast power chords, aggressive energy',
    mixNotes: 'Loud and aggressive, not too much separation'
  },
  {
    id: 'slide-guitar',
    name: 'Slide Guitar',
    category: 'lead',
    subCategory: 'electric guitar',
    genres: ['southern rock', 'blues', 'country rock', 'swamp'],
    stereoPosition: { default: 55, canPan: true, zone: 'right' },
    frequencyRange: { low: 150, high: 7000, focus: 'high-mid', competesWidth: ['pedal-steel'] },
    role: 'melodic color',
    promptHints: 'crying slide guitar, bottleneck, sustaining notes',
    mixNotes: 'Slightly forward when featured, singing quality'
  },

  // ═══════════════════════════════════════════════════════════════
  // ACOUSTIC GUITARS
  // ═══════════════════════════════════════════════════════════════
  {
    id: 'acoustic-guitar-strummed',
    name: 'Strummed Acoustic',
    category: 'rhythm',
    subCategory: 'acoustic guitar',
    genres: ['country', 'folk', 'americana', 'southern rock'],
    stereoPosition: { default: -35, canPan: true, zone: 'left' },
    frequencyRange: { low: 80, high: 8000, focus: 'mid', competesWidth: [] },
    role: 'harmonic bed',
    promptHints: 'driving acoustic strum, open chords, rhythmic anchor',
    mixNotes: 'Warm but defined, complements electric guitar'
  },
  {
    id: 'acoustic-guitar-fingerpicked',
    name: 'Fingerpicked Acoustic',
    category: 'texture',
    subCategory: 'acoustic guitar',
    genres: ['folk', 'ballad', 'singer-songwriter', 'country'],
    stereoPosition: { default: 25, canPan: true, zone: 'center' },
    frequencyRange: { low: 80, high: 8000, focus: 'mid', competesWidth: [] },
    role: 'melodic texture',
    promptHints: 'delicate fingerpicking, intricate patterns, intimate',
    mixNotes: 'Close mic, intimate feel, detailed'
  },
  {
    id: 'acoustic-guitar-dark',
    name: 'Dark Acoustic',
    category: 'texture',
    subCategory: 'acoustic guitar',
    genres: ['doom', 'folk noir', 'dark americana'],
    stereoPosition: { default: -20, canPan: true, zone: 'left' },
    frequencyRange: { low: 80, high: 5000, focus: 'low-mid', competesWidth: [] },
    role: 'atmospheric texture',
    promptHints: 'dark acoustic guitar, low-tuned, ominous picking',
    mixNotes: 'Darker EQ, more low-mid focus, haunting'
  },

  // ═══════════════════════════════════════════════════════════════
  // KEYS / ORGANS
  // ═══════════════════════════════════════════════════════════════
  {
    id: 'hammond-b3',
    name: 'Hammond B3 Organ',
    category: 'keys',
    subCategory: 'organ',
    genres: ['southern rock', 'blues', 'gospel', 'rock'],
    stereoPosition: { default: 0, spread: 50, canPan: true, zone: 'wide' },
    frequencyRange: { low: 100, high: 8000, focus: 'mid', competesWidth: ['piano-honkytonk'] },
    role: 'harmonic bed',
    promptHints: 'swirling Hammond B3, Leslie speaker, warm and gritty',
    mixNotes: 'Wide stereo from Leslie, fills harmonic space, not too loud'
  },
  {
    id: 'hammond-b3-dirty',
    name: 'Dirty Hammond',
    category: 'keys',
    subCategory: 'organ',
    genres: ['doom', 'stoner rock', 'psych'],
    stereoPosition: { default: 0, spread: 40, canPan: true, zone: 'wide' },
    frequencyRange: { low: 80, high: 6000, focus: 'low-mid', competesWidth: [] },
    role: 'atmospheric texture',
    promptHints: 'dirty overdriven organ, growling, menacing',
    mixNotes: 'More distorted, sits with heavy guitars'
  },
  {
    id: 'hammond-b3-gospel',
    name: 'Gospel Organ',
    category: 'keys',
    subCategory: 'organ',
    genres: ['gospel', 'soul', 'r&b'],
    stereoPosition: { default: 0, spread: 60, canPan: true, zone: 'wide' },
    frequencyRange: { low: 80, high: 8000, focus: 'mid', competesWidth: ['piano-gospel'] },
    role: 'harmonic foundation',
    promptHints: 'soaring gospel organ, full stops, powerful swells',
    mixNotes: 'Full and present, works with piano, supports choir'
  },
  {
    id: 'piano-honkytonk',
    name: 'Honky-Tonk Piano',
    category: 'keys',
    subCategory: 'piano',
    genres: ['country', 'outlaw country', 'rockabilly', 'blues'],
    stereoPosition: { default: 30, spread: 40, canPan: true, zone: 'right' },
    frequencyRange: { low: 80, high: 12000, focus: 'mid', competesWidth: ['hammond-b3'] },
    role: 'harmonic color',
    promptHints: 'barrelhouse honky-tonk piano, slightly out of tune, percussive',
    mixNotes: 'Bright and percussive, sits alongside guitars'
  },
  {
    id: 'piano-gospel',
    name: 'Gospel Piano',
    category: 'keys',
    subCategory: 'piano',
    genres: ['gospel', 'soul', 'r&b'],
    stereoPosition: { default: -25, spread: 50, canPan: true, zone: 'left' },
    frequencyRange: { low: 60, high: 12000, focus: 'full-spectrum', competesWidth: ['hammond-b3-gospel'] },
    role: 'harmonic foundation',
    promptHints: 'powerful gospel piano, full chords, runs and fills',
    mixNotes: 'Foundational, works with organ, dynamic range'
  },
  {
    id: 'piano-ballad',
    name: 'Ballad Piano',
    category: 'keys',
    subCategory: 'piano',
    genres: ['ballad', 'soft rock', 'pop'],
    stereoPosition: { default: 0, spread: 70, canPan: true, zone: 'wide' },
    frequencyRange: { low: 60, high: 10000, focus: 'full-spectrum', competesWidth: [] },
    role: 'harmonic foundation',
    promptHints: 'emotional grand piano, sustained chords, delicate touch',
    mixNotes: 'Wide stereo, intimate verses, bigger in choruses'
  },
  {
    id: 'clavinet',
    name: 'Clavinet',
    category: 'keys',
    subCategory: 'electric piano',
    genres: ['funk', 'disco', 'groove'],
    stereoPosition: { default: -35, canPan: true, zone: 'left' },
    frequencyRange: { low: 150, high: 6000, focus: 'high-mid', competesWidth: ['electric-guitar-funk'] },
    role: 'rhythmic color',
    promptHints: 'funky clavinet, wah effect, percussive stabs',
    mixNotes: 'Percussive and cutting, rhythmic focal point with guitar'
  },
  {
    id: 'synthesizer-pad',
    name: 'Synth Pad',
    category: 'keys',
    subCategory: 'synthesizer',
    genres: ['doom', 'metal', 'ambient', '80s'],
    stereoPosition: { default: 0, spread: 100, canPan: true, zone: 'wide' },
    frequencyRange: { low: 60, high: 10000, focus: 'mid', competesWidth: [] },
    role: 'atmospheric texture',
    promptHints: 'ambient synth pad, atmospheric wash, ominous swells',
    mixNotes: 'Very wide, sits behind everything, adds depth'
  },
  {
    id: 'synthesizer-pad-80s',
    name: '80s Synth Pad',
    category: 'keys',
    subCategory: 'synthesizer',
    genres: ['glam', '80s rock', 'synth-rock'],
    stereoPosition: { default: 0, spread: 90, canPan: true, zone: 'wide' },
    frequencyRange: { low: 80, high: 12000, focus: 'mid', competesWidth: [] },
    role: 'atmospheric texture',
    promptHints: 'lush 80s synth pad, analog warmth, big reverb',
    mixNotes: 'Wide and lush, supports guitars and vocals'
  },

  // ═══════════════════════════════════════════════════════════════
  // COLOR INSTRUMENTS
  // ═══════════════════════════════════════════════════════════════
  {
    id: 'pedal-steel',
    name: 'Pedal Steel Guitar',
    category: 'color',
    subCategory: 'steel guitar',
    genres: ['country', 'outlaw country', 'americana'],
    stereoPosition: { default: 50, canPan: true, zone: 'right' },
    frequencyRange: { low: 200, high: 8000, focus: 'high-mid', competesWidth: ['slide-guitar'] },
    role: 'melodic color',
    promptHints: 'weeping pedal steel, crying bends, emotional sustain',
    mixNotes: 'Featured when playing, sits behind vocals, adds emotion'
  },
  {
    id: 'harmonica',
    name: 'Harmonica',
    category: 'color',
    subCategory: 'wind',
    genres: ['blues', 'country', 'folk', 'southern rock'],
    stereoPosition: { default: 0, canPan: true, zone: 'center' },
    frequencyRange: { low: 250, high: 6000, focus: 'mid', competesWidth: [] },
    role: 'melodic color',
    promptHints: 'wailing blues harmonica, expressive bends, rhythmic chugs',
    mixNotes: 'Center when featured, cuts through mix'
  },
  {
    id: 'harmonica-blues',
    name: 'Blues Harp',
    category: 'color',
    subCategory: 'wind',
    genres: ['blues', 'blues rock', 'swamp blues'],
    stereoPosition: { default: 0, canPan: true, zone: 'center' },
    frequencyRange: { low: 300, high: 5500, focus: 'mid', competesWidth: [] },
    role: 'melodic lead',
    promptHints: 'dirty blues harp, amplified, Little Walter style',
    mixNotes: 'Through amp simulation, gritty, vocal-like presence'
  },
  {
    id: 'fiddle',
    name: 'Fiddle',
    category: 'color',
    subCategory: 'strings',
    genres: ['country', 'bluegrass', 'folk', 'celtic'],
    stereoPosition: { default: -40, canPan: true, zone: 'left' },
    frequencyRange: { low: 200, high: 10000, focus: 'high-mid', competesWidth: ['mandolin'] },
    role: 'melodic color',
    promptHints: 'lively country fiddle, double stops, breakdown solos',
    mixNotes: 'Bright and present, featured during solos'
  },
  {
    id: 'mandolin',
    name: 'Mandolin',
    category: 'color',
    subCategory: 'strings',
    genres: ['country', 'bluegrass', 'folk', 'americana'],
    stereoPosition: { default: 45, canPan: true, zone: 'right' },
    frequencyRange: { low: 300, high: 10000, focus: 'high', competesWidth: ['fiddle'] },
    role: 'rhythmic color',
    promptHints: 'chiming mandolin, rhythmic chops, melodic fills',
    mixNotes: 'Bright, percussive strums, adds high-end shimmer'
  },
  {
    id: 'horns-section',
    name: 'Horn Section',
    category: 'color',
    subCategory: 'brass',
    genres: ['funk', 'soul', 'r&b', 'blues'],
    stereoPosition: { default: 0, spread: 60, canPan: true, zone: 'wide' },
    frequencyRange: { low: 100, high: 8000, focus: 'mid', competesWidth: [] },
    role: 'punctuation',
    promptHints: 'punchy horn hits, staccato stabs, melodic lines',
    mixNotes: 'Wide spread, punchy, punctuates key moments'
  },
  {
    id: 'strings-section',
    name: 'String Section',
    category: 'color',
    subCategory: 'strings',
    genres: ['ballad', 'gospel', 'epic', 'orchestral'],
    stereoPosition: { default: 0, spread: 100, canPan: false, zone: 'wide' },
    frequencyRange: { low: 80, high: 12000, focus: 'full-spectrum', competesWidth: [] },
    role: 'atmospheric texture',
    promptHints: 'lush strings, swelling crescendos, emotional depth',
    mixNotes: 'Very wide, swells in choruses, adds cinematic quality'
  },
  {
    id: 'cello',
    name: 'Cello',
    category: 'color',
    subCategory: 'strings',
    genres: ['doom', 'dark', 'orchestral', 'ballad'],
    stereoPosition: { default: 25, canPan: true, zone: 'center' },
    frequencyRange: { low: 65, high: 5000, focus: 'low-mid', competesWidth: [] },
    role: 'melodic texture',
    promptHints: 'mournful cello, sustained notes, dark melody',
    mixNotes: 'Warm and present, adds emotional depth'
  },
  {
    id: 'choir-subtle',
    name: 'Subtle Choir',
    category: 'vocal',
    subCategory: 'choir',
    genres: ['ballad', 'epic', 'gospel'],
    stereoPosition: { default: 0, spread: 100, canPan: false, zone: 'wide' },
    frequencyRange: { low: 150, high: 10000, focus: 'mid', competesWidth: [] },
    role: 'atmospheric texture',
    promptHints: 'ethereal choir, ooohs and aaahs, background support',
    mixNotes: 'Behind everything, adds size, subtle presence'
  }
];

/**
 * Get instruments by genre
 */
export function getInstrumentsByGenre(genre: string): Instrument[] {
  const normalizedGenre = genre.toLowerCase();
  return INSTRUMENT_LIBRARY.filter(inst => 
    inst.genres.some(g => g.toLowerCase().includes(normalizedGenre))
  );
}

/**
 * Get instrument by ID
 */
export function getInstrumentById(id: string): Instrument | undefined {
  return INSTRUMENT_LIBRARY.find(i => i.id === id);
}

/**
 * Get instruments by category
 */
export function getInstrumentsByCategory(category: string): Instrument[] {
  return INSTRUMENT_LIBRARY.filter(i => i.category === category);
}

/**
 * Get multiple instruments by IDs
 */
export function getInstrumentsByIds(ids: string[]): Instrument[] {
  return ids.map(id => getInstrumentById(id)).filter((i): i is Instrument => i !== undefined);
}
